/**
 * KPI Indicator Component
 *
 * Renders a single big-number tile from `kpi.resolve` payload.
 *
 * Mock data is ONLY used in storybook/preview mode (indicatorId omitted).
 * When a real `indicatorId` is passed, we show the live payload — or an
 * explicit empty state if the resolver returned nothing.
 */

import { useMemo } from 'react';
import { Badge } from '../../../components/ui/badge';
import { AlertCircle, Loader2, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { generateMockData, MockKPIData } from '../utils';
import { useIndicatorData } from './useIndicatorData';

interface KPIIndicatorProps {
  indicatorId?: number;
}

interface ResolvedKPI {
  value: string;
  label: string;
  unit: string;
  target: number | null;
  change: number | null;
}

export function KPIIndicator({ indicatorId }: KPIIndicatorProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);
  const isPreviewMode = indicatorId === undefined;

  const resolved: ResolvedKPI | null = useMemo(() => {
    if (live?.kpi) {
      const k = live.kpi;
      const numericValue = typeof k.value === 'number' ? k.value : Number(k.value);
      const display = Number.isFinite(numericValue)
        ? numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : String(k.value ?? '—');
      return {
        value: display,
        label: k.label || '',
        unit: k.unit || '',
        target: typeof k.target === 'number' ? k.target : null,
        change: typeof k.change === 'number' ? k.change : null,
      };
    }
    // Only use random mock data in storybook/preview mode. For real indicators
    // we show an empty state so the user knows the indicator needs configuring.
    if (isPreviewMode) {
      const mock = generateMockData('kpi') as MockKPIData;
      return {
        value: `${mock.value}%`,
        label: 'Performance',
        unit: '%',
        target: mock.target,
        change: parseFloat(mock.change),
      };
    }
    return null;
  }, [live, isPreviewMode]);

  if (loading && !resolved) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mb-2" />
        <span className="text-xs">Loading…</span>
      </div>
    );
  }

  if (!resolved) {
    const message = live?.message || 'Configure a Y field in the builder to see a value.';
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground text-center">
        <AlertCircle className="h-5 w-5 mb-2" />
        <div className="text-sm font-medium">Not configured</div>
        <div className="text-xs mt-1 max-w-[240px]">{message}</div>
      </div>
    );
  }

  const change = resolved.change;
  const isPositive = change !== null && change > 0;
  const isNegative = change !== null && change < 0;
  const trendColor = isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-muted-foreground';
  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  const onTarget = resolved.target !== null && resolved.change !== null
    ? resolved.change >= 0
    : null;

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="text-4xl font-bold mb-1">
        {resolved.value}
        {resolved.unit && <span className="text-2xl text-muted-foreground ml-1">{resolved.unit}</span>}
      </div>
      {resolved.label && (
        <div className="text-sm text-muted-foreground">{resolved.label}</div>
      )}
      {change !== null && (
        <div className="flex items-center gap-1 text-sm mt-2">
          <TrendIcon className={`h-4 w-4 ${trendColor}`} />
          <span className={trendColor}>
            {isPositive ? '+' : ''}{change.toFixed(1)}%
          </span>
        </div>
      )}
      {resolved.target !== null && (
        <div className="text-xs text-muted-foreground mt-1">
          Target: {resolved.target}{resolved.unit}
        </div>
      )}
      {onTarget !== null && (
        <Badge className={`mt-2 ${onTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {onTarget ? 'On Target' : 'Below Target'}
        </Badge>
      )}
    </div>
  );
}
